import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Search as SearchIcon } from '@styled-icons/bootstrap/Search';
import { Check as CheckIcon } from '@styled-icons/boxicons-regular/Check';

import {
  Box,
  Badge,
  Button,
  CloseButton,
  Divider,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';

import useDebouncedEffect from 'hooks/useDebouncedEffect';

import { Budget } from 'types/budget';
import { DefaultPrice, State, StatePrice } from 'types/composition';

import {
  BaseCompositionSearchResult,
  searchCompositionsByBase,
  searchCompositionsAcrossBases,
} from './service';

interface ModalBudgetSuggestedCompositionLinkProps extends ModalRootProps {
  data: {
    bases: Budget['bases'];
    initialSearchTerm: string;
  };
}

const SEARCH_DEBOUNCE_MS = 400;
const BADGE_TOTAL_CAP = 99;

function isDefaultPrice(price: StatePrice): price is DefaultPrice {
  return 'default' in price;
}

const getStatePrice = (prices: Record<string, State> | undefined): State => {
  if (!prices) {
    return {
      default: {
        total: 0,
      },
    } as State;
  }

  const states = Object.keys(prices);
  const state = states[0];

  return prices[state];
};

const getCompositionPrice = (
  prices: Record<string, State> | undefined,
): number => {
  const statePrice = getStatePrice(prices);

  if (isDefaultPrice(statePrice)) {
    return statePrice.default.total;
  }

  if (statePrice.taxed) {
    return statePrice.taxed.total;
  }

  if (statePrice.untaxed) {
    return statePrice.untaxed.total;
  }

  return 0;
};

const getBadgeTotalLabel = (total: number): string => {
  if (total > BADGE_TOTAL_CAP) {
    return `${BADGE_TOTAL_CAP}+`;
  }

  return String(total);
};

const ModalBudgetSuggestedCompositionLink: React.FC<
  ModalBudgetSuggestedCompositionLinkProps
> = ({ data, onConfirm, handleClose, ...restProps }) => {
  const bases = useMemo(() => data?.bases || [], [data?.bases]);
  const [searchTerm, setSearchTerm] = useState(
    () => data?.initialSearchTerm || '',
  );
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [resultsByBase, setResultsByBase] = useState<
    Record<number, BaseCompositionSearchResult & { loading: boolean }>
  >({});

  const requestIdRef = useRef(0);
  const lastSearchedTermRef = useRef<string | null>(null);
  const currentSearchTermRef = useRef(searchTerm);

  const searchTermTrimmed = useMemo(() => searchTerm.trim(), [searchTerm]);

  useEffect(() => {
    currentSearchTermRef.current = searchTerm;
  }, [searchTerm]);

  const buildBaseFallbackResult = useCallback(
    (base: Budget['bases'][number]) => {
      return {
        base,
        compositions: [],
        error: false,
        page: 1,
        lastPage: 1,
        total: 0,
        loading: false,
      };
    },
    [],
  );

  const buildResultMap = useCallback(
    (results: BaseCompositionSearchResult[]) => {
      return results.reduce<
        Record<number, BaseCompositionSearchResult & { loading: boolean }>
      >((acc, result) => {
        acc[result.base.id] = {
          ...result,
          loading: false,
        };
        return acc;
      }, {});
    },
    [],
  );

  const searchAcrossBases = useCallback(
    async (
      term: string,
      opts: {
        focusFirstTabWithResults?: boolean;
      } = {},
    ) => {
      const normalized = term.trim();

      if (!normalized || bases.length === 0) {
        setResultsByBase({});
        setActiveTab(0);
        setLoading(false);
        return;
      }

      requestIdRef.current += 1;
      const currentRequestId = requestIdRef.current;

      setLoading(true);

      const result = await searchCompositionsAcrossBases(bases, normalized, 1);

      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      setResultsByBase(buildResultMap(result));
      setActiveTab((oldTab) => {
        const firstTabWithResults = result.findIndex(
          (baseResult) => baseResult.compositions.length > 0,
        );

        if (
          opts.focusFirstTabWithResults &&
          firstTabWithResults >= 0 &&
          firstTabWithResults <= bases.length - 1
        ) {
          return firstTabWithResults;
        }

        if (oldTab <= bases.length - 1) {
          return oldTab;
        }

        return 0;
      });
      setLoading(false);
    },
    [bases, buildResultMap],
  );

  const handlePaginateBase = useCallback(
    async (base: Budget['bases'][number], targetPage: number) => {
      const normalized = searchTerm.trim();

      if (!normalized || targetPage < 1) {
        return;
      }

      requestIdRef.current += 1;
      const currentRequestId = requestIdRef.current;

      setResultsByBase((oldState) => {
        const currentBaseState =
          oldState[base.id] || buildBaseFallbackResult(base);
        return {
          ...oldState,
          [base.id]: {
            ...currentBaseState,
            loading: true,
          },
        };
      });

      const result = await searchCompositionsByBase(
        base,
        normalized,
        targetPage,
      );

      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      setResultsByBase((oldState) => ({
        ...oldState,
        [base.id]: {
          ...result,
          loading: false,
        },
      }));
    },
    [searchTerm, buildBaseFallbackResult],
  );

  useEffect(() => {
    const term = currentSearchTermRef.current;
    searchAcrossBases(term);
    lastSearchedTermRef.current = term.trim();
  }, [searchAcrossBases]);

  useDebouncedEffect(
    () => {
      const normalizedTerm = searchTerm.trim();

      if (normalizedTerm === lastSearchedTermRef.current) {
        return;
      }

      lastSearchedTermRef.current = normalizedTerm;
      searchAcrossBases(searchTerm, { focusFirstTabWithResults: true });
    },
    SEARCH_DEBOUNCE_MS,
    [searchAcrossBases, searchTerm],
  );

  const renderEmptyState = (): JSX.Element | null => {
    if (!searchTermTrimmed) {
      return (
        <Box py={8} textAlign="center">
          <Text color="gray.500">
            Digite uma descrição para buscar serviços.
          </Text>
        </Box>
      );
    }

    if (loading) {
      return (
        <Flex py={8} justifyContent="center">
          <Spinner size="sm" />
        </Flex>
      );
    }

    if (bases.length === 0) {
      return (
        <Box py={8} textAlign="center">
          <Text color="gray.500">Nenhuma base configurada para pesquisa.</Text>
        </Box>
      );
    }

    return null;
  };

  return (
    <Modal {...restProps} size="5xl">
      <ModalOverlay />
      <ModalContent bg="transparent" boxShadow="none">
        <ModalBody pb={0} bg="transparent">
          <Flex justifyContent="flex-end" mb={2}>
            <CloseButton
              size="lg"
              color="white"
              bg="blackAlpha.100"
              _hover={{ bg: 'blackAlpha.700' }}
              onClick={handleClose}
            />
          </Flex>

          <Box
            borderWidth="1px"
            borderColor="gray.200"
            bg="white"
            borderRadius="md"
            overflow="hidden"
          >
            <Box p={4} borderBottomWidth="1px" borderColor="gray.100">
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <Icon as={SearchIcon} color="gray.500" />
                </InputLeftElement>
                <Input
                  placeholder="Busque por descrição do serviço..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  autoFocus
                />
              </InputGroup>
            </Box>

            <Box maxH="65vh" overflowY="auto">
              {renderEmptyState()}

              {!loading && bases.length > 0 && searchTermTrimmed && (
                <Tabs
                  index={activeTab}
                  onChange={setActiveTab}
                  isLazy
                  variant="unstyled"
                >
                  {bases.length > 1 && (
                    <Box
                      px={3}
                      py={2}
                      borderBottomWidth="1px"
                      borderColor="gray.100"
                    >
                      <TabList
                        overflowX="auto"
                        sx={{ '&::-webkit-scrollbar': { display: 'none' } }}
                      >
                        {bases.map((base) => {
                          const result = resultsByBase[base.id];
                          const hasResult = (result?.total || 0) > 0;
                          const total = result?.total || 0;
                          const totalLabel = getBadgeTotalLabel(total);
                          const shouldCapTotal = total > BADGE_TOTAL_CAP;
                          return (
                            <Tab
                              key={base.id}
                              borderRadius="md"
                              mr={2}
                              px={4}
                              py={2}
                              bg="gray.50"
                              color="gray.700"
                              _selected={{
                                bg: 'blue.500',
                                color: 'white',
                              }}
                              // _focus={{ boxShadow: 'none' }}
                            >
                              <HStack spacing={2}>
                                <Text fontSize="sm" fontWeight="600">
                                  {base.version.base.description}
                                </Text>
                                <Badge
                                  borderRadius="full"
                                  display="flex"
                                  justifyContent="center"
                                  alignItems="center"
                                  minWidth="5"
                                  height="5"
                                  px={2}
                                  colorScheme={hasResult ? 'red' : 'gray'}
                                  variant="solid"
                                  fontSize="xs"
                                >
                                  {shouldCapTotal ? (
                                    <Tooltip
                                      label={String(total)}
                                      hasArrow
                                      placement="top"
                                    >
                                      <Box>{totalLabel}</Box>
                                    </Tooltip>
                                  ) : (
                                    totalLabel
                                  )}
                                </Badge>
                              </HStack>
                            </Tab>
                          );
                        })}
                      </TabList>
                    </Box>
                  )}

                  <TabPanels>
                    {bases.map((base) => {
                      const result =
                        resultsByBase[base.id] || buildBaseFallbackResult(base);
                      const baseTitle = `${base.version.base.description} - ${base.version.description}`;

                      return (
                        <TabPanel key={base.id} p={0}>
                          <Box px={4} py={3} bg="gray.50">
                            <Text fontWeight="bold" fontSize="sm">
                              {baseTitle}
                            </Text>
                            {result.error && (
                              <Text color="red.500" fontSize="xs">
                                Não foi possível buscar serviços nesta base.
                              </Text>
                            )}
                          </Box>

                          {result.loading && (
                            <Flex py={8} justifyContent="center">
                              <Spinner size="sm" />
                            </Flex>
                          )}

                          {!result.loading &&
                            result.compositions.length === 0 &&
                            !result.error && (
                              <Box px={4} py={8} textAlign="center">
                                <Text color="gray.500" fontSize="sm">
                                  Nenhum serviço encontrado nesta base.
                                </Text>
                              </Box>
                            )}

                          {!result.loading && (
                            <VStack align="stretch" spacing={0}>
                              {result.compositions.map((composition) => (
                                <React.Fragment key={composition._id}>
                                  <Flex
                                    px={4}
                                    py={3}
                                    gap={4}
                                    justifyContent="space-between"
                                    alignItems="center"
                                  >
                                    <Box flex="1" minW={0}>
                                      <Text
                                        fontWeight="medium"
                                        fontSize="sm"
                                        noOfLines={2}
                                      >
                                        {composition._source.description}
                                      </Text>
                                      <Text color="gray.600" fontSize="sm">
                                        {Intl.NumberFormat('pt-BR', {
                                          style: 'currency',
                                          currency: 'BRL',
                                        }).format(
                                          getCompositionPrice(
                                            composition._source.prices,
                                          ),
                                        )}
                                      </Text>
                                    </Box>

                                    <HStack>
                                      <Button
                                        size="sm"
                                        colorScheme="green"
                                        leftIcon={<Icon as={CheckIcon} />}
                                        onClick={() => {
                                          if (onConfirm) {
                                            onConfirm(composition);
                                          }
                                          handleClose();
                                        }}
                                      >
                                        Selecionar
                                      </Button>
                                    </HStack>
                                  </Flex>
                                  <Divider />
                                </React.Fragment>
                              ))}
                            </VStack>
                          )}

                          {!result.loading && result.lastPage > 1 && (
                            <Flex
                              px={4}
                              py={3}
                              alignItems="center"
                              justifyContent="center"
                              gap={3}
                              borderTopWidth="1px"
                              borderColor="gray.100"
                            >
                              <Button
                                size="xs"
                                variant="ghost"
                                onClick={() =>
                                  handlePaginateBase(base, result.page - 1)
                                }
                                isDisabled={result.page <= 1}
                              >
                                Anterior
                              </Button>
                              <Text fontSize="xs" color="gray.600">
                                Página {result.page} de {result.lastPage}
                              </Text>
                              <Button
                                size="xs"
                                variant="ghost"
                                onClick={() =>
                                  handlePaginateBase(base, result.page + 1)
                                }
                                isDisabled={result.page >= result.lastPage}
                              >
                                Próxima
                              </Button>
                            </Flex>
                          )}
                        </TabPanel>
                      );
                    })}
                  </TabPanels>
                </Tabs>
              )}
            </Box>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ModalBudgetSuggestedCompositionLink;
